import { X } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { SheetSectionCard } from '@/components/SheetSectionCard'
import { SheetRowNavButtons } from '@/components/SheetRowNavButtons'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { usePhoneMessagesQuery } from '@/hooks/usePhoneMessagesQuery'
import { formatPhoneForDisplay } from '@/utils/phone'
import { formatSheetTimestamp } from '@/lib/format'
import { MessageBubbles } from './MessageBubbles'
import { MessageSheetContext } from './MessageSheetContext'
import type { MessageRecord } from '../types'

interface MessageThreadSheetProps {
  phone: string | null
  onOpenChange: (open: boolean) => void
  message?: MessageRecord
  navigation?: ReactNode
}

/** Shared phone conversation sheet, also opened from a call's Texts section. */
export function MessageThreadSheet({
  phone,
  onOpenChange,
  message,
  navigation,
}: MessageThreadSheetProps) {
  const {
    data,
    isLoading: loadingThread,
    isError,
    error,
    refetch,
    isFetching,
  } = usePhoneMessagesQuery(phone)
  const displayPhone = phone ? formatPhoneForDisplay(phone) : ''
  const patientNames = data?.patient_names.join(', ') ?? ''

  return (
    <Sheet open={!!phone} onOpenChange={onOpenChange}>
      <SheetContent
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="bg-neutral-ui-bg flex w-full max-w-none flex-col gap-0 rounded-l-xl p-0 sm:max-w-lg"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left">
              {message ? (
                <span className="text-primary-high-contrast text-lg font-semibold">
                  {formatSheetTimestamp(message.created_at)}
                </span>
              ) : (
                patientNames || displayPhone || 'Conversation'
              )}
            </SheetTitle>
            {navigation}
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {message ? <MessageSheetContext message={message} /> : null}
          {!message && patientNames && displayPhone ? (
            <span className="text-muted-foreground text-xs">
              {displayPhone}
            </span>
          ) : null}
        </SheetHeader>
        <SheetDescription className="sr-only">
          Full conversation with {displayPhone}
        </SheetDescription>

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          {phone ? (
            <SheetSectionCard
              className="flex min-h-0 flex-1 flex-col overflow-hidden px-0"
              title={
                <span className="flex items-center justify-between gap-2 px-3">
                  Conversation History
                  <TabRefreshButton
                    label="Refresh conversation"
                    isFetching={isFetching}
                    onRefresh={() => void refetch()}
                  />
                </span>
              }
              body={
                <div className="min-h-0 flex-1">
                  {loadingThread ? (
                    <div className="flex h-full items-center justify-center">
                      <Spinner />
                    </div>
                  ) : isError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center text-sm">
                      <p className="text-destructive">
                        {error instanceof Error
                          ? error.message
                          : 'Failed to load messages'}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void refetch()}>
                        Retry
                      </Button>
                    </div>
                  ) : !data?.messages.length ? (
                    <p className="text-muted-foreground py-8 text-center text-sm">
                      No messages in this conversation.
                    </p>
                  ) : (
                    <MessageBubbles
                      key={message?.id ?? phone}
                      messages={data.messages}
                      selectedMessageId={message?.id}
                    />
                  )}
                </div>
              }
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/** Row selection and keyboard navigation match the Calls table. */
export function MessageLogsSheetWrapper() {
  const { table, rowSelection, isLoading } = useDataTable<
    MessageRecord,
    unknown
  >()
  const navigation = useSheetRowNavigation(table, rowSelection, !!isLoading)
  const message = navigation.selectedRow?.original
  return (
    <MessageThreadSheet
      phone={message?.phone ?? null}
      message={message}
      onOpenChange={navigation.onOpenChange}
      navigation={<SheetRowNavButtons navigation={navigation} noun="message" />}
    />
  )
}
